import type { ComponentType, PropsWithChildren } from 'react';
import type React from 'react';

import { TrackingContext } from '@change-corgi/core-react/tracking';

import type { ErrorStateDetails } from 'src/app/shared/utils/async';
import { isError, isLoaded } from 'src/app/shared/utils/async';

import type { TopicPageCombinedState, TopicPagePrefetchedContext } from './hook';
import { usePageContext } from './hook';
import { TopicPageDataContextProvider } from './pageData';
import { TopicPagePaginatedPetitionUpdatesDataContextProvider } from './pagePetitionUpdatesPaginatedData';
import { TopicPagePaginatedRecentPetitionsDataContextProvider } from './pageRecentPetitionsPaginatedData';
import { TopicSupporterVoicesDataContextProvider } from './pageSupporterVoiceData';
import { TopicPagePaginatedTrendingPetitionsDataContextProvider } from './pageTrendingPetitionsPaginatedData';
import { TopicPagePaginatedVictoriousPetitionsDataContextProvider } from './pageVictoriousPetitionsPaginatedData';

export type ErrorHandlerProps = { context: ErrorStateDetails<TopicPageCombinedState> };

type Props = {
	slug: string;
	prefetchedData: TopicPagePrefetchedContext | undefined;
	errorHandler: ComponentType<ErrorHandlerProps>;
	loadingFallback: JSX.Element;
};

export const TopicPageContextProvider: React.FC<PropsWithChildren<Props>> = ({
	children,
	slug,
	prefetchedData,
	errorHandler: ErrorHandler,
	loadingFallback,
}) => {
	const pageContextState = usePageContext(slug, prefetchedData);

	if (isError(pageContextState)) {
		const { status, ...rest } = pageContextState;
		return <ErrorHandler context={rest} />;
	}
	if (!isLoaded(pageContextState)) return loadingFallback;

	const {
		pageData,
		petitionUpdatesPaginatedData,
		recentPetitionsPaginatedData,
		trendingPetitionsPaginatedData,
		victoriousPetitionsPaginatedData,
		supporterVoicesData,
	} = pageContextState;

	return (
		<TopicPageDataContextProvider pageData={pageData}>
			<TopicPagePaginatedRecentPetitionsDataContextProvider recentPetitionsPaginatedData={recentPetitionsPaginatedData}>
				<TopicPagePaginatedTrendingPetitionsDataContextProvider
					trendingPetitionsPaginatedData={trendingPetitionsPaginatedData}
				>
					<TopicPagePaginatedVictoriousPetitionsDataContextProvider
						victoriousPetitionsPaginatedData={victoriousPetitionsPaginatedData}
					>
						<TopicPagePaginatedPetitionUpdatesDataContextProvider
							petitionUpdatesPaginatedData={petitionUpdatesPaginatedData}
						>
							<TopicSupporterVoicesDataContextProvider supporterVoicesData={supporterVoicesData}>
								<TrackingContext properties={{ topic_id: pageData.data.id }}>{children}</TrackingContext>
							</TopicSupporterVoicesDataContextProvider>
						</TopicPagePaginatedPetitionUpdatesDataContextProvider>
					</TopicPagePaginatedVictoriousPetitionsDataContextProvider>
				</TopicPagePaginatedTrendingPetitionsDataContextProvider>
			</TopicPagePaginatedRecentPetitionsDataContextProvider>
		</TopicPageDataContextProvider>
	);
};
